import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/store/auth';
import { useCanState } from '@/hooks/useEmployeeRights';
import { useInsurerPermissions } from '@/hooks/useInsurerPermissions';
import type { RightsLevel, RightsModule } from '@/services/employeeRights';
import { isRoleAllowed } from './isRoleAllowed';

export interface RouteRightRequirement {
  module: RightsModule;
  level: RightsLevel;
}

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: string[];
  /**
   * B4: the module/level the screen needs, on top of the role list. Only ever
   * narrows `allowedRoles`; admin roles and roles outside the rights model pass
   * (see `useCan`). The API enforces the same cell -- this keeps a user whose
   * right was revoked from landing on a screen full of 403s.
   */
  requires?: RouteRightRequirement;
  /**
   * #643: the insurer permission code ("module.level") an insurer EMPLOYEE's
   * custom role must hold. Admin roles and every non-insurer role pass (see
   * `useInsurerPermissions`); the API refuses the same call.
   */
  insurerRequires?: string;
}

function Spinner() {
  return (
    <div className="min-h-screen flex items-center justify-center" role="status" aria-label="Loading">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
    </div>
  );
}

export function ProtectedRoute({ children, allowedRoles, requires, insurerRequires }: ProtectedRouteProps) {
  const { isAuthenticated, user, isLoading } = useAuthStore();
  const location = useLocation();

  if (isLoading) {
    return <Spinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // The role check runs FIRST: a role the route never admits is refused without
  // waiting on (or spending) a rights request.
  if (allowedRoles && user && !isRoleAllowed(allowedRoles, user.role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (requires) {
    return <RightsGate requires={requires}>{children}</RightsGate>;
  }

  if (insurerRequires) {
    return <InsurerRightsGate code={insurerRequires}>{children}</InsurerRightsGate>;
  }

  return <>{children}</>;
}

/** #643: waits for the insurer role's answer, then admits or redirects. */
function InsurerRightsGate({ code, children }: { code: string; children: React.ReactNode }) {
  const { can, isLoading, isError } = useInsurerPermissions();
  if (isLoading) return <Spinner />;
  if (isError) return <RightsCheckFailed />;
  if (!can(code)) return <Navigate to="/unauthorized" replace />;
  return <>{children}</>;
}

/**
 * A separate component so the rights query only mounts for routes that
 * declare `requires`. It waits for the first answer instead of bouncing: before
 * it, `useCan` reads false, and redirecting on that would send every gated user
 * to /unauthorized on each hard refresh (plan §3 L10).
 */
function RightsGate({ requires, children }: { requires: RouteRightRequirement; children: React.ReactNode }) {
  const { allowed, isLoading, isError, retry } = useCanState(requires.module, requires.level);
  if (isLoading) return <Spinner />;
  // A failed rights request is not a refusal. Redirecting (with `replace`) on
  // it would tell a user caught by a gunicorn reload or a 5xx that they are
  // denied, and take Back away from them. Stay on the URL and offer a retry;
  // the hook's 60 s interval and window focus retry on their own as well.
  if (isError) return <RightsCheckFailed onRetry={retry} />;
  if (!allowed) return <Navigate to="/unauthorized" replace />;
  return <>{children}</>;
}

function RightsCheckFailed({ onRetry }: { onRetry?: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div
        role="alert"
        className="w-full max-w-md rounded-lg border border-amber-200 bg-amber-50 p-6 text-center dark:border-amber-800 dark:bg-amber-900/20"
      >
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          We could not check your access to this page
        </h2>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          This is a connection problem, not a refusal. Try again in a moment.
        </p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-4 inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
          >
            Try again
          </button>
        )}
      </div>
    </div>
  );
}
